/**
 * The predicate algebra: evaluation, and the account of *why* something failed.
 *
 * Two functions matter here. `evaluate` answers true or false and is the hot
 * path — the solver calls it millions of times. `collectFailures` answers the
 * far more interesting question the hint ladder, the near-miss feedback, and
 * the solver's dead-end reporting all depend on: which specific leaves of a
 * rule that *nearly* fired did not hold, and which selected particle is
 * responsible.
 *
 * Discrete predicates were chosen over a weighted score vector precisely
 * because this second function is possible at all (design D2). A weight cannot
 * be explained back to the player; a failing leaf can.
 *
 * ## Empty ordinal axes
 *
 * `max` and `min` evaluate **false** when nothing in the assembly carries a
 * value on the axis. Absence is not a low value: the vocabulary declares
 * explicit floors (`risk: none`, `cost: zero`, `evidence: none`) for authors who
 * mean "nothing to worry about here", and `empty`/`present` for authors who mean
 * "the subject never came up". An author wanting either reading writes
 * `{ any: [{ empty: { axis } }, { max: … }] }` and says so.
 */
import { ordinalIndex, } from '@managing-up/schema';
import { countOf, distinctOn, isOrdinal, maxOrdinal, minOrdinal, tagSetEntries, valuesOn, } from './tags.js';
/**
 * Whether the `Per My Last Email` perk excuses this predicate.
 *
 * The perk's promise is that its opener "overrides node temperature" — the
 * node's disposition filter, which is always written on the `tone` axis. The
 * engine reads that as: while the perk's opener is in the assembly, a leaf that
 * measures `tone` is satisfied whatever the assembly actually carries.
 *
 * A bare `not` wrapping such a leaf is excused too, and deliberately: a rule
 * saying "so long as they were not aggressive" is the same disposition filter
 * written from the other side, and a perk that beat one but not the other would
 * be a perk about authoring style. Combinators are *not* excused — `all` and
 * `any` recurse, so each child is judged on its own, and a rule that wants tone
 * *and* a costed number still wants the number.
 */
function toneOverridden(predicate, context) {
    if (!context.toneOverride)
        return false;
    if (has(predicate, 'not')) {
        const inner = predicate.not;
        return axisOf(inner) === 'tone' && !has(inner, 'proposal');
    }
    // `proposal.has` reads the travelling pitch's tags rather than this
    // assembly's, and no opener of the player's is answerable for those.
    if (has(predicate, 'proposal'))
        return false;
    return axisOf(predicate) === 'tone';
}
/** Numeric comparison for every leaf that carries an `op`. */
export function compare(op, left, right) {
    switch (op) {
        case 'eq':
            return left === right;
        case 'ne':
            return left !== right;
        case 'lt':
            return left < right;
        case 'lte':
            return left <= right;
        case 'gt':
            return left > right;
        case 'gte':
            return left >= right;
    }
}
/**
 * A predicate is a one-key object drawn from a closed union. Reading it as a
 * record is the narrowing: the union has no discriminant field to switch on.
 */
const asRecord = (predicate) => predicate;
const has = (predicate, key) => Object.hasOwn(asRecord(predicate), key);
/** Position of `value` in an ordinal axis's declared order, or `-1`. */
const positionOf = (axis, value) => ordinalIndex(axis, value);
function evaluateOrdinal(aggregate, leaf, pick) {
    const axis = leaf.axis;
    // A nominal axis has no declared order to compare against. The vocabulary
    // pass rejects this at load, so reaching here means the pack was never
    // loaded through `createGame`; fail closed rather than invent an ordering.
    if (!isOrdinal(axis))
        return false;
    const actual = pick === 'max' ? maxOrdinal(aggregate, axis) : minOrdinal(aggregate, axis);
    if (actual < 0)
        return false;
    const threshold = positionOf(axis, leaf.value);
    if (threshold < 0)
        return false;
    return compare(leaf.op, actual, threshold);
}
/** True when the predicate holds against the context. */
export function evaluate(predicate, context) {
    if (toneOverridden(predicate, context))
        return true;
    if (has(predicate, 'all')) {
        return predicate.all.every((child) => evaluate(child, context));
    }
    if (has(predicate, 'any')) {
        return predicate.any.some((child) => evaluate(child, context));
    }
    if (has(predicate, 'not')) {
        return !evaluate(predicate.not, context);
    }
    if (has(predicate, 'has')) {
        const leaf = predicate.has;
        return countOf(context.aggregate, leaf.axis, leaf.value) > 0;
    }
    if (has(predicate, 'none')) {
        const leaf = predicate.none;
        return countOf(context.aggregate, leaf.axis, leaf.value) === 0;
    }
    if (has(predicate, 'count')) {
        const leaf = predicate.count;
        return compare(leaf.op, countOf(context.aggregate, leaf.axis, leaf.value), leaf.n);
    }
    if (has(predicate, 'max')) {
        return evaluateOrdinal(context.aggregate, predicate.max, 'max');
    }
    if (has(predicate, 'min')) {
        return evaluateOrdinal(context.aggregate, predicate.min, 'min');
    }
    if (has(predicate, 'distinct')) {
        const leaf = predicate
            .distinct;
        return compare(leaf.op, distinctOn(context.aggregate, leaf.axis), leaf.n);
    }
    if (has(predicate, 'empty')) {
        const leaf = predicate.empty;
        return valuesOn(context.aggregate, leaf.axis).length === 0;
    }
    if (has(predicate, 'present')) {
        const leaf = predicate.present;
        return valuesOn(context.aggregate, leaf.axis).length > 0;
    }
    if (has(predicate, 'hasParticle')) {
        const id = predicate.hasParticle;
        return context.selection.some((slot) => slot.particleId === id);
    }
    if (has(predicate, 'slotParticle')) {
        const leaf = predicate.slotParticle;
        return context.selection.some((slot) => slot.n === leaf.slot && slot.particleId === leaf.id);
    }
    if (has(predicate, 'identity')) {
        return context.identity === predicate.identity;
    }
    if (has(predicate, 'nodeState')) {
        const leaf = predicate.nodeState;
        const nodeId = leaf.nodeId ?? context.resolvingNodeId;
        const actual = context.nodeVars[nodeId]?.[leaf.var];
        if (actual === undefined)
            return false;
        return compare(leaf.op, actual, leaf.value);
    }
    if (has(predicate, 'proposal')) {
        return evaluateProposal(asRecord(predicate)['proposal'], context);
    }
    if (has(predicate, 'phase')) {
        return context.phase === predicate.phase;
    }
    if (has(predicate, 'day')) {
        const leaf = predicate.day;
        return compare(leaf.op, context.day, leaf.n);
    }
    if (has(predicate, 'channel')) {
        return context.channelId === predicate.channel;
    }
    if (has(predicate, 'flag')) {
        return context.flags.includes(predicate.flag);
    }
    if (has(predicate, 'strikes')) {
        const leaf = predicate.strikes;
        return compare(leaf.op, context.strikes, leaf.n);
    }
    // An object carrying no known leaf cannot be satisfied. The schema pass
    // rejects these before a pack ever loads.
    return false;
}
function evaluateProposal(leaf, context) {
    const proposal = context.proposal;
    if (Object.hasOwn(leaf, 'has')) {
        const inner = leaf['has'];
        return countOf(proposal.tags, inner.axis, inner.value) > 0;
    }
    if (Object.hasOwn(leaf, 'origin'))
        return proposal.origin === leaf['origin'];
    if (Object.hasOwn(leaf, 'hops')) {
        const inner = leaf['hops'];
        return compare(inner.op, proposal.hops, inner.n);
    }
    if (Object.hasOwn(leaf, 'attribution'))
        return proposal.attribution === leaf['attribution'];
    return false;
}
// ---------------------------------------------------------------------------
// Failure explanation
// ---------------------------------------------------------------------------
/** The axis a leaf names, when it names one. */
function axisOf(predicate) {
    for (const key of ['has', 'none', 'count', 'max', 'min', 'distinct', 'empty', 'present']) {
        if (!has(predicate, key))
            continue;
        const leaf = asRecord(predicate)[key];
        return leaf?.axis ?? null;
    }
    if (has(predicate, 'proposal')) {
        const inner = asRecord(predicate)['proposal'];
        if (Object.hasOwn(inner, 'has'))
            return inner['has'].axis;
    }
    return null;
}
/** The value a leaf names, when it names one. */
function valueOf(predicate) {
    for (const key of ['has', 'none', 'count', 'max', 'min']) {
        if (!has(predicate, key))
            continue;
        const leaf = asRecord(predicate)[key];
        return leaf?.value ?? null;
    }
    if (has(predicate, 'proposal')) {
        const inner = asRecord(predicate)['proposal'];
        if (Object.hasOwn(inner, 'has'))
            return inner['has'].value;
        if (Object.hasOwn(inner, 'attribution'))
            return String(inner['attribution']);
    }
    return null;
}
/** Which leaf operator this is, for the trace and for authored hint selection. */
function leafNameOf(predicate) {
    return Object.keys(asRecord(predicate))[0] ?? 'unknown';
}
/**
 * The values *present in the assembly* that are responsible for a leaf being
 * false.
 *
 * A leaf can fail two ways, and only one of them has a culprit. `has cost zero`
 * fails because nothing carried it — there is no particle to point at, and the
 * hint ladder must say "something is missing", not blame a slot. `none tone
 * aggressive` fails because something *did* carry it, and that particle is
 * exactly what tier 3 names.
 */
function offendingValues(predicate, aggregate) {
    if (has(predicate, 'none')) {
        const leaf = predicate.none;
        return countOf(aggregate, leaf.axis, leaf.value) > 0 ? [leaf.value] : [];
    }
    if (has(predicate, 'count')) {
        const leaf = predicate.count;
        const actual = countOf(aggregate, leaf.axis, leaf.value);
        // Too many of it is the assembly's fault; too few of it is an absence.
        return actual > leaf.n && actual > 0 ? [leaf.value] : [];
    }
    if (has(predicate, 'max') || has(predicate, 'min')) {
        const pick = has(predicate, 'max') ? 'max' : 'min';
        const leaf = asRecord(predicate)[pick];
        const axis = leaf.axis;
        if (!isOrdinal(axis))
            return [];
        const threshold = positionOf(axis, leaf.value);
        if (threshold < 0)
            return [];
        const present = valuesOn(aggregate, axis);
        // Only values on the wrong side of the threshold are culprits. When the
        // comparison failed for want of a value at all, there are none.
        const culprit = (value) => {
            const position = positionOf(axis, value);
            if (position < 0)
                return false;
            switch (leaf.op) {
                case 'lt':
                    return position >= threshold;
                case 'lte':
                    return position > threshold;
                case 'gt':
                case 'gte':
                    return false;
                case 'eq':
                    return pick === 'max' ? position > threshold : position < threshold;
                case 'ne':
                    return position === threshold;
            }
        };
        return dedupe(present.filter(culprit));
    }
    if (has(predicate, 'empty')) {
        const leaf = predicate.empty;
        return dedupe(valuesOn(aggregate, leaf.axis));
    }
    if (has(predicate, 'distinct')) {
        const leaf = predicate
            .distinct;
        const actual = distinctOn(aggregate, leaf.axis);
        return actual > leaf.n ? dedupe(valuesOn(aggregate, leaf.axis)) : [];
    }
    return [];
}
const dedupe = (values) => {
    const seen = [];
    for (const value of values)
        if (!seen.includes(value))
            seen.push(value);
    return seen;
};
/** Selected particles carrying any of `values` on `axis`, with the slots they sit in. */
function contributors(context, axis, values) {
    if (axis === null || values.length === 0)
        return { particleIds: [], slots: [] };
    const particleIds = [];
    const slots = [];
    for (const slot of context.selection) {
        const particle = context.particles[slot.particleId];
        if (particle === undefined)
            continue;
        const carries = tagSetEntries(particle.tags).some(([entryAxis, entryValue]) => entryAxis === axis && values.includes(entryValue));
        if (!carries)
            continue;
        particleIds.push(slot.particleId);
        slots.push(slot.n);
    }
    return { particleIds, slots: [...slots].sort((a, b) => a - b) };
}
function describe(predicate, context, pointer) {
    const axis = axisOf(predicate);
    // `proposal.has` names an axis, but on the proposal's tags rather than this
    // assembly's, so no selected particle is answerable for it.
    const onAssembly = !has(predicate, 'proposal');
    const values = onAssembly ? offendingValues(predicate, context.aggregate) : [];
    const { particleIds, slots } = contributors(context, onAssembly ? axis : null, values);
    return {
        pointer,
        leaf: leafNameOf(predicate),
        axis,
        value: valueOf(predicate),
        contributingParticleIds: particleIds,
        contributingSlots: slots,
    };
}
/**
 * The leaves that account for `predicate` being false, in evaluation order.
 *
 * Combinators are walked rather than reported: a failing `all` blames only the
 * children that failed, while a failing `any` blames all of them, because
 * satisfying any one would have been enough. A `not` that failed is reported as
 * itself, carrying the inner leaf's axis and the particle that satisfied it.
 *
 * Returns an empty array when the predicate is true.
 */
export function collectFailures(predicate, context, pointer = '') {
    if (evaluate(predicate, context))
        return [];
    if (has(predicate, 'all')) {
        const children = predicate.all;
        const failures = [];
        children.forEach((child, index) => {
            if (evaluate(child, context))
                return;
            failures.push(...collectFailures(child, context, `${pointer}/all/${String(index)}`));
        });
        return failures;
    }
    if (has(predicate, 'any')) {
        const children = predicate.any;
        const failures = [];
        children.forEach((child, index) => {
            failures.push(...collectFailures(child, context, `${pointer}/any/${String(index)}`));
        });
        return failures;
    }
    if (has(predicate, 'not')) {
        const inner = predicate.not;
        const axis = axisOf(inner);
        const value = valueOf(inner);
        // The inner predicate held; the assembly is guilty of satisfying it.
        const values = value !== null ? [value] : axis !== null ? dedupe(valuesOn(context.aggregate, axis)) : [];
        const { particleIds, slots } = contributors(context, axis, values);
        return [
            {
                pointer: `${pointer}/not`,
                leaf: 'not',
                axis,
                value,
                contributingParticleIds: particleIds,
                contributingSlots: slots,
            },
        ];
    }
    return [describe(predicate, context, pointer)];
}
//# sourceMappingURL=predicates.js.map